<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'account',
    path: '/accounts/:id',
    component: 'Account',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Account',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
      default: null,
    },
    ownerId: {
      type: String,
      required: false,
      default: null,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return props.oid == null
      ? $fn.value.schemaDefaults('CreateAccount', {
          owner: { id: props.ownerId },
        })
      : $fn.value.fetch('read_account', {
          asResource: true,
          parameters: { id: props.oid },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title:
          props.oid == null
            ? $fn.value.gettext('New Cash or Bank Account')
            : [item.value.snapshot('name'), $fn.value.gettext('Account')],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.ownerId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('ownerId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_srgsmfqct_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadSlickgrid_t2446iaij_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const act_1040167445267876_setData_vrn71x3m2_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_vrn71x3m2_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_emit_j7e9fgc78_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_1040167445267876_closeModal_uqugx3614_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_e8qi5ci2x_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'accounts',
    };
  };
  const act_1040167445267876_crud_o1egxggic_getActionArgs = async ($event) => {
    return {
      objectType: 'account',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_7789867021953041_reloadSlickgrid_5l1dvvi86_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'accounts',
    };
  };
  const act_2434514588044777_closeModal_vk8w1tl8p_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_srgsmfqct =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_srgsmfqct_getActionArgs,
    });
  const inst_evh_7315092382398562_close_l1nt66gle =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_srgsmfqct,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_l1nt66gle],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e01h7xyz4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_kbg1by6da =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e01h7xyz4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_kbg1by6da],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_4fn2eic4r =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_8q5tzalpi =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_4fn2eic4r,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_8q5tzalpi],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_59nlju8g4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_luoc1w67j =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_59nlju8g4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_luoc1w67j],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_ogzwctmwo =
    new actionTypes.CloseComponentAction({});
  const inst_evh_6466705138206980_deleted_en6dyh053 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_ogzwctmwo,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadSlickgrid_t2446iaij =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6466705138206980_reloadSlickgrid_t2446iaij_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_cogycwl4h =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadSlickgrid_t2446iaij,
      event: 'deleted',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_en6dyh053,
      inst_evh_6466705138206980_deleted_cogycwl4h,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_setData_vrn71x3m2 = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_vrn71x3m2_getActionArgs,
    when: act_1040167445267876_setData_vrn71x3m2_getWhen,
  });
  const inst_evh_1040167445267876_success_jrcr79xi3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_vrn71x3m2,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_emit_j7e9fgc78 = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_j7e9fgc78_getActionArgs,
  });
  const inst_evh_1040167445267876_success_krzgjsld8 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_j7e9fgc78,
      event: 'success',
      displayName: 'emit',
    });
  const act_1040167445267876_closeModal_uqugx3614 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_uqugx3614_getActionArgs,
    });
  const inst_evh_1040167445267876_success_dd10zighb =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_uqugx3614,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_reloadSlickgrid_e8qi5ci2x =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_e8qi5ci2x_getActionArgs,
    });
  const inst_evh_1040167445267876_success_wv4jw6l8c =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_e8qi5ci2x,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_crud_o1egxggic = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_o1egxggic_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_success_jrcr79xi3,
      inst_evh_1040167445267876_success_krzgjsld8,
      inst_evh_1040167445267876_success_dd10zighb,
      inst_evh_1040167445267876_success_wv4jw6l8c,
    ],
  });
  const inst_evh_1040167445267876_submit_vfmnzrwju =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_o1egxggic,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_vfmnzrwju],
    event: 'submit',
  });
  const act_7789867021953041_closeModal_4g0k08bp2 =
    new actionTypes.CloseModalAction({});
  const inst_evh_7789867021953041_saved_c6dwklngw =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_closeModal_4g0k08bp2,
      event: 'saved',
      displayName: 'closeModal',
    });
  const act_7789867021953041_reloadSlickgrid_5l1dvvi86 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_7789867021953041_reloadSlickgrid_5l1dvvi86_getActionArgs,
    });
  const inst_evh_7789867021953041_saved_525mhvs3s =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_reloadSlickgrid_5l1dvvi86,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_7789867021953041_saved = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7789867021953041_saved_c6dwklngw,
      inst_evh_7789867021953041_saved_525mhvs3s,
    ],
    event: 'saved',
  });
  const act_2434514588044777_closeModal_vk8w1tl8p =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_vk8w1tl8p_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_2qh7ysyh9 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_vk8w1tl8p,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_2qh7ysyh9],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_l1nt66gle,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_kbg1by6da,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_8q5tzalpi,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_luoc1w67j,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_en6dyh053,
    inst_evh_6466705138206980_deleted_cogycwl4h,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_jrcr79xi3,
    inst_evh_1040167445267876_success_krzgjsld8,
    inst_evh_1040167445267876_success_dd10zighb,
    inst_evh_1040167445267876_success_wv4jw6l8c,
    inst_evh_1040167445267876_submit_vfmnzrwju,
    save: inst_evh_1040167445267876_submit_vfmnzrwju,
    inst_evh_1040167445267876_submit,
    inst_evh_7789867021953041_saved_c6dwklngw,
    inst_evh_7789867021953041_saved_525mhvs3s,
    inst_evh_7789867021953041_saved,
    inst_evh_2434514588044777_clickPreventStop_2qh7ysyh9,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_l1nt66gle = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_l1nt66gle.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_kbg1by6da = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_kbg1by6da.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_8q5tzalpi = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_8q5tzalpi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_luoc1w67j = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_luoc1w67j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_en6dyh053 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_en6dyh053.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_cogycwl4h = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_cogycwl4h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_jrcr79xi3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_jrcr79xi3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_krzgjsld8 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_krzgjsld8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_dd10zighb = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_dd10zighb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_wv4jw6l8c = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_wv4jw6l8c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_vfmnzrwju = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_vfmnzrwju.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_saved_c6dwklngw = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_saved_c6dwklngw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_saved_525mhvs3s = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_saved_525mhvs3s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_saved = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_2qh7ysyh9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_2qh7ysyh9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Account"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcAccountHeader
          :account="item"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcAccountHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="account"
        :readonly="item.locked"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetAccountGeneral
            :item="item"
            :formId="formId"
            no-submit-button
            @saved="evh_7789867021953041_saved($event, {})"
          >
          </FieldsetAccountGeneral>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          type="submit"
          :text="gettext('Save')"
          :form="forms.account"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
